/** Universal date/time parsing for mySky Assistant — format: YYMMDD · HHMM (max 23:59). */
import { todayISO, nowTime, TZ } from './utils.js';

export const DATE_HINT = 'YYMMDD or YYYY-MM-DD (e.g. 260723)';
export const TIME_HINT = 'HHMM or HH:MM · 00:00–23:59';
export const DATETIME_HINT = 'YYMMDD HHMM (e.g. 260723 1745) or YYYY-MM-DD HH:MM';

/** Display date for terminal shortcuts — e.g. 2026:07:24 */
export function nowDisplayDate() {
  return todayISO().replace(/-/g, ':');
}

/** Display date + time for terminal shortcuts — e.g. 2026:07:24 16:42 */
export function nowDisplayDateTime() {
  return `${nowDisplayDate()} ${nowTime()}`;
}

/** Compact date for terminal input — e.g. 260724 */
export function nowCompactDate() {
  const [y, m, d] = todayISO().split('-');
  return `${y.slice(2)}${m}${d}`;
}

/** Compact time for terminal input — e.g. 1640 */
export function nowCompactTime() {
  return nowTime().replace(':', '');
}

/** Compact date + time for terminal input — e.g. 260724 1640 */
export function nowCompactDateTime() {
  return `${nowCompactDate()} ${nowCompactTime()}`;
}

export function currentYearPrefix() {
  return todayISO().slice(0, 2);
}

export function isSkip(text) {
  const s = String(text || '').trim().toLowerCase();
  return !s || s === 'skip' || s === '-' || s === 'none' || s === 'n/a';
}

export function parseSmartTime(raw, opts = {}) {
  const maxHour = Number.isFinite(opts.maxHour) ? opts.maxHour : 23;
  const maxMinute = Number.isFinite(opts.maxMinute) ? opts.maxMinute : 59;
  let s = String(raw || '').trim();
  if (!s || isSkip(s)) return null;
  if (/^\d{3,4}$/.test(s)) {
    s = s.padStart(4, '0');
    s = `${s.slice(0, 2)}:${s.slice(2)}`;
  }
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  if (h * 60 + min > maxHour * 60 + maxMinute) return null;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

export function validateSmartTime(raw) {
  if (isSkip(raw)) return null;
  return parseSmartTime(raw) ? null : `Invalid time. Use ${TIME_HINT}.`;
}

export function parseSmartDate(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (!s || isSkip(s)) return null;
  if (s === 'today') return todayISO();
  if (s === 'tomorrow') {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toLocaleDateString('en-CA', { timeZone: TZ });
  }
  const rel = s.match(/^\+(\d+)\s*(day|days|d)?$/);
  if (rel) {
    const d = new Date();
    d.setDate(d.getDate() + Number(rel[1]));
    return d.toLocaleDateString('en-CA', { timeZone: TZ });
  }
  const compact = s.replace(/\s+/g, '');
  if (/^\d{6}$/.test(compact)) {
    const yy = compact.slice(0, 2);
    const mm = compact.slice(2, 4);
    const dd = compact.slice(4, 6);
    const iso = `${currentYearPrefix()}${yy}-${mm}-${dd}`;
    if (!isValidIsoDate(iso)) return null;
    return iso;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return isValidIsoDate(s) ? s : null;
  }
  if (/^\d{4}:\d{2}:\d{2}$/.test(s)) {
    const iso = s.replace(/:/g, '-');
    return isValidIsoDate(iso) ? iso : null;
  }
  return null;
}

function isValidIsoDate(iso) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

export function validateSmartDate(raw, { required = true } = {}) {
  if (isSkip(raw)) return required ? 'Date is required.' : null;
  return parseSmartDate(raw) ? null : `Invalid date. Use ${DATE_HINT}.`;
}

/** Parse YYMMDD HHMM, YYYY-MM-DD HH:MM, compact forms → ISO datetime `YYYY-MM-DDTHH:MM` */
export function parseSmartDateTime(raw, opts = {}) {
  const s = String(raw || '').trim().toLowerCase();
  if (!s || isSkip(s)) return null;
  if (s === 'today') return `${todayISO()}T17:00`;
  if (s === 'tomorrow') {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const day = d.toLocaleDateString('en-CA', { timeZone: TZ });
    return `${day}T17:00`;
  }

  const rel = s.match(/^\+(\d+)\s*(day|days|d)?(?:\s+(\d{3,4}|\d{1,2}:\d{2}))?$/);
  if (rel) {
    const d = new Date();
    d.setDate(d.getDate() + Number(rel[1]));
    const day = d.toLocaleDateString('en-CA', { timeZone: TZ });
    const time = rel[2] ? parseSmartTime(rel[2], opts) : '17:00';
    return time ? `${day}T${time}` : null;
  }

  const spaced = s.trim().match(/^(\d{6}|\d{4}-\d{2}-\d{2}|\d{4}:\d{2}:\d{2})\s+(\d{3,4}|\d{1,2}:\d{2})$/);
  if (spaced) {
    const day = parseSmartDate(spaced[1]);
    const time = parseSmartTime(spaced[2], opts);
    if (day && time) return `${day}T${time}`;
    return null;
  }

  const compact = s.replace(/\s+/g, '');
  if (/^\d{10}$/.test(compact)) {
    const day = parseSmartDate(compact.slice(0, 6));
    const time = parseSmartTime(compact.slice(6), opts);
    if (day && time) return `${day}T${time}`;
    return null;
  }
  if (/^\d{12}$/.test(compact)) {
    const day = parseSmartDate(compact.slice(0, 8).replace(/^(\d{4})(\d{2})(\d{2})$/, '$1-$2-$3'));
    const time = parseSmartTime(compact.slice(8), opts);
    if (day && time) return `${day}T${time}`;
    return null;
  }

  if (/^\d{4}-\d{2}-\d{2}t\d{2}:\d{2}$/i.test(s)) {
    return `${s.slice(0, 10)}T${s.slice(11, 16)}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s}T17:00`;

  const dayOnly = parseSmartDate(s);
  if (dayOnly) return `${dayOnly}T17:00`;
  return null;
}

export function parseSmartDateOnly(raw) {
  const dt = parseSmartDateTime(raw);
  if (dt) return dt.split('T')[0];
  return parseSmartDate(raw);
}

export function validateSmartDateTime(raw, { required = true } = {}) {
  if (isSkip(raw)) return required ? 'Date & time required.' : null;
  return parseSmartDateTime(raw) ? null : `Invalid date/time. Use ${DATETIME_HINT}.`;
}

export function datetimeToDate(isoDt) {
  if (!isoDt) return null;
  return String(isoDt).trim().split('T')[0] || null;
}
